// filePreviewService (E7.2.1) — cliente do dispatcher de preview (E7.1.2) +
// helpers puros que o chat usa pra classificar anexos e endereçar o preview.
//
// O endpoint é `GET /api/v1/file-preview/{storage_key:path}`. O chat não guarda
// `storage_key` no AttachmentContent (só a `url` assinada), então o derivamos do
// path da própria url — ver `storageKeyFromSignedUrl`.

import api from "./api";
import type { FilePreviewResponse } from "@/types/filePreview";

export type PreviewKind = "image" | "pdf" | "csv" | "markdown" | "json" | "office";

/**
 * Limite de tamanho p/ preview de Office (E7.2.2 / FR-E7-10). Espelha o
 * `LIBREOFFICE_WORKER_MAX_MB` do backend — acima disso o dispatcher devolve 413.
 * Usado pra alertar proativamente na bolha (sem round-trip) antes de oferecer
 * "Visualizar". Se o backend mudar o limite, sincronize aqui.
 */
export const MAX_PREVIEW_MB = 50;
export const MAX_PREVIEW_BYTES = MAX_PREVIEW_MB * 1024 * 1024;

// Segmento que precede o storage_key numa proxy url assinada
// (`/api/v1/storage/file/{key}?expires=&sig=`).
const STORAGE_FILE_MARKER = "/storage/file/";

/**
 * Extrai o `storage_key` (mantido URL-encoded, como veio no path) de uma signed
 * proxy url. Aceita url relativa (`/api/v1/...`) ou absoluta. Retorna `null` se a
 * url não casar com o padrão do proxy (→ a bolha cai no link genérico, sem preview).
 *
 * O key fica encoded de propósito: é passado simétrico ao route `{storage_key:path}`
 * (o FastAPI decodifica), evitando double-encoding de filenames com espaço/acento.
 */
export function storageKeyFromSignedUrl(url: string | undefined | null): string | null {
  if (!url) return null;
  const q = url.indexOf("?");
  const pathname = q >= 0 ? url.slice(0, q) : url;
  const marker = pathname.indexOf(STORAGE_FILE_MARKER);
  if (marker < 0) return null;
  const key = pathname.slice(marker + STORAGE_FILE_MARKER.length);
  return key.length > 0 ? key : null;
}

const EXT_KIND: Record<string, PreviewKind> = {
  csv: "csv",
  tsv: "csv",
  md: "markdown",
  markdown: "markdown",
  json: "json",
};

// Office detectável por extensão (uploads costumam marcar octet-stream) ou por
// MIME — espelha a allowlist OOXML/legacy/ODF do dispatcher backend.
const OFFICE_EXTS = new Set(["docx", "xlsx", "pptx", "doc", "xls", "ppt", "odt", "ods", "odp"]);

const OFFICE_MIMES = new Set([
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.presentation",
]);

function extensionOf(filename: string | undefined | null): string {
  if (!filename) return "";
  const dot = filename.lastIndexOf(".");
  return dot >= 0 ? filename.slice(dot + 1).toLowerCase() : "";
}

/**
 * Decide a afford de preview na bolha do chat. Espelha a desambiguação do
 * dispatcher: imagem/pdf por MIME; csv/md/json e Office por extensão (o upload
 * costuma marcar `text/plain`/`application/octet-stream` nesses). `null` = sem
 * preview inline → link de download genérico. O `render_hint` autoritativo
 * (magic-bytes) vem na resposta do dispatcher e é quem decide o renderer de fato;
 * `"office"` sempre vira PDF (`status=ready`) após a conversão (E7.2.2).
 */
export function classifyPreview(
  mime: string | undefined | null,
  filename: string | undefined | null
): PreviewKind | null {
  const m = (mime || "").toLowerCase();
  if (m.startsWith("image/")) return "image";
  if (m === "application/pdf") return "pdf";
  const ext = extensionOf(filename);
  const byExt = EXT_KIND[ext];
  if (byExt) return byExt;
  // Office (E7.2.2): preview via conversão LibreOffice→PDF async.
  if (OFFICE_EXTS.has(ext) || OFFICE_MIMES.has(m)) return "office";
  // Uploads que marcam o MIME textual correto mesmo sem extensão reconhecível.
  if (m === "text/csv") return "csv";
  if (m === "text/markdown") return "markdown";
  if (m === "application/json") return "json";
  return null;
}

/** Pede o preview ao dispatcher. `storageKey` já vem URL-encoded (ver acima). */
export async function getPreview(storageKey: string): Promise<FilePreviewResponse> {
  const { data } = await api.get<FilePreviewResponse>(`/file-preview/${storageKey}`);
  return data;
}

const filePreviewService = { getPreview, storageKeyFromSignedUrl, classifyPreview };
export default filePreviewService;
